import intl from 'react-intl-universal';
import {
    forwardRef,
    useEffect,
    useImperativeHandle,
    useRef,
    useState,
    createContext,
} from 'react';
// import { Form } from 'antd';
import type {
    ProFormInstance,
    ProFormColumnsType,
    ProFormProps,
    ProFormLayoutType,
    ModalFormProps,
    // FormSchema,
} from '@ant-design/pro-components';
import { ProConfigProvider } from '@ant-design/pro-components';
import { ProForm, BetaSchemaForm } from '@ant-design/pro-components';
import getValueMap, { ValueType } from './formItem/index';
import { message } from 'antd';
import './index.less';
import { isArray, debounce } from 'lodash';
type FormProps<valueTypeKey> = Omit<ProFormProps, 'action' | 'formRef'> &
    Omit<ModalFormProps, 'action' | 'formRef'> & {
        columns: ProFormColumnsType<any, ValueType>[];
        layoutType?: ProFormLayoutType;
        valueTypeKey?: valueTypeKey;
    };

/**
 * 如果传递参数层级太深，可使用这个 context，但是要做好兼容
 * 使用 context 必须定义好类型
 */
export const FormConfigContext = createContext<{
    // 工单分类额外参数，目前只在乐园使用
    category?: {
        isPopLandTab: number;
    };
}>({});

const FormConfigProvider = (props: any) => {
    return (
        <ProConfigProvider
            valueTypeMap={{
                ...getValueMap(props),
                ...(props.valueTypeMap || {}),
            }}
        >
            {props.children}
        </ProConfigProvider>
    );
};
{
    /* <ValueTypeKey> */
}
const DynamicForm: { (props: FormProp): JSX.Element } = forwardRef(
    (props: FormProps, ref: ProFormInstance) => {
        const {
            id = 'dynamicForm',
            layoutType = 'Form',
            children,
            readonly = false,
            submitter,
            form: propsForm,
            columns: originColums,
            onFinish = () => {},
            ...otherProps
        } = props;
        const [form] = ProForm.useForm();
        const formRef = useRef<ProFormInstance>(propsForm || form);
        const [columns, setColumns] = useState<
            ProFormColumnsType<any, ValueType | ValueTypeKey>[]
        >([]);

        useEffect(() => {
            setColumns(formatColumns(originColums));
        }, [originColums]);

        const formatColumns: (
            columns: ProFormColumnsType<any, ValueType>[],
        ) => ProFormColumnsType<any, ValueType>[] = (columns) => {
            return (
                columns?.map((col) => {
                    const { valueType = 'text' } = col;
                    if (!col.fieldProps) {
                        col.fieldProps = {};
                    }

                    if (
                        !col.renderFormItem &&
                        col.valueType !== 'formList' &&
                        col.valueType !== 'group'
                    ) {
                        col.fieldProps = {
                            allowClear: true,
                            ...col.fieldProps,
                        };
                    }

                    if (
                        ['select', 'category', 'cascader'].includes(
                            valueType,
                        ) ||
                        valueType?.includes('date')
                    ) {
                        col.fieldProps = {
                            showSearch:
                                col.fieldProps.showSearch === undefined
                                    ? true
                                    : col.fieldProps.showSearch,
                            getPopupContainer: () =>
                                document.getElementById(id),
                            ...col.fieldProps,
                        };
                    }

                    if (isArray(col.columns)) {
                        return {
                            ...col,
                            columns: formatColumns(col.columns),
                        };
                    }
                    return col;
                }) || []
            );
        };

        useImperativeHandle(ref, () => {
            return {
                ...formRef.current,
                validateFields(...args: any[]) {
                    return formRef.current
                        ?.validateFields(...args)
                        .catch((err: any) => {
                            formRef.current?.scrollToField(
                                err?.errorFields?.[0]?.name,
                            );
                            if (
                                err?.errorFields?.some(
                                    (field) =>
                                        field?.errors?.includes(
                                            intl.get('此为必填项'),
                                        ) ||
                                        field?.errors?.includes(
                                            intl.get('此项为必填项'),
                                        ),
                                )
                            ) {
                                message.error(intl.get('必填项未填写!'));
                            }
                            return Promise.reject(err);
                        });
                },
                validateFieldsReturnFormatValue(...args: any[]) {
                    return formRef.current
                        ?.validateFieldsReturnFormatValue?.(...args)
                        .catch((err: any) => {
                            formRef.current?.scrollToField(
                                err?.errorFields?.[0]?.name,
                            );

                            if (
                                err?.errorFields?.some(
                                    (field) =>
                                        field?.errors?.includes(
                                            intl.get('此为必填项'),
                                        ) ||
                                        field?.errors?.includes(
                                            intl.get('此项为必填项'),
                                        ),
                                )
                            ) {
                                message.error(intl.get('必填项未填写!'));
                            }
                            return Promise.reject(err);
                        });
                },
            };
        });

        const onFinishFailed = (error: any) => {
            console.log('onFinishFailed', error);
            message.error(intl.get('请检查表单填写是否正确'));
            otherProps?.onFinishFailed?.(error);
            // const element = document.getElementById('questionDesc');
            // console.log(element);
            // element?.scrollIntoView();
            formRef.current?.scrollToField(error?.errorFields?.[0]?.name);
        };

        return (
            <FormConfigProvider {...otherProps}>
                <BetaSchemaForm<any, ValueType>
                    id={id}
                    form={propsForm || form}
                    formRef={formRef}
                    layoutType={layoutType}
                    layout="horizontal"
                    colon={true}
                    grid={true}
                    submitter={readonly ? false : submitter}
                    readonly={readonly}
                    size="middle"
                    labelWrap={true}
                    labelCol={{ span: 3 }}
                    autoFocusFirstInput={false}
                    scrollToFirstError={{
                        behavior: 'smooth',
                        skipOverflowHiddenElements: true,
                        block: 'start',
                    }}
                    columns={columns}
                    {...otherProps}
                    className={`${otherProps.className} dynamicForm`}
                    onFinishFailed={onFinishFailed}
                    onFinish={debounce(onFinish, 1000)}
                >
                    {children}
                </BetaSchemaForm>
            </FormConfigProvider>
        );
    },
);

export default DynamicForm;

export { FormConfigProvider };

export type FormItemValueType = ValueType;
